
<template>
  <Page :fill="false">
    <Card>
      <template #header>
        <h1 class="text-xl font-bold text-primary flex items-center">
          <i class="fa-solid fa-user-circle mr-2"></i>
          User Profile
        </h1>
      </template>
      <div class="flex flex-col md:flex-row">
        <!-- Profile Picture Section -->
        <div class="w-full md:w-1/3 flex flex-col items-center mb-6 md:mb-0">
          <div class="w-32 h-32 rounded-full overflow-hidden mb-4 relative flex items-center justify-center">
            <Avatar :label="userInitials" shape="circle" :class="userAvatarColor + ' w-32! h-32! text-4xl! font-bold! text-white!'" />
          </div>
          <h3 class="text-xl font-bold text-text">{{ profile.name }}</h3>
          <p class="text-text-light text-sm">{{ profile.designation }}</p>
        </div>

        <!-- User Details Form -->
        <div class="w-full md:w-2/3 md:pl-8 md:border-l border-border">
          <!-- Edit Profile Section -->
          <div class="mb-6">
            <h4 class="text-text font-medium mb-4 flex items-center">
              <i class="fa-solid fa-edit mr-2"></i>
              Edit Profile
            </h4>
            <form @submit.prevent="saveProfile" class="space-y-4">
              <div>
                <label class="block text-text-light text-sm mb-1" for="name-input">Name</label>
                <input id="name-input" v-model="editProfile.name" type="text"
                  class="w-full bg-background border-0 rounded-full px-4 py-3 focus:ring-2 focus:ring-primary focus:outline-hidden text-sm text-text"
                  autocomplete="off" />
              </div>
              <div>
                <label class="block text-text-light text-sm mb-1" for="designation-input">Designation</label>
                <input id="designation-input" v-model="editProfile.designation" type="text"
                  class="w-full bg-background border-0 rounded-full px-4 py-3 focus:ring-2 focus:ring-primary focus:outline-hidden text-sm text-text"
                  autocomplete="off" />
              </div>
              <div>
                <label class="block text-text-light text-sm mb-1" for="email-input">E-mail</label>
                <input
                  id="email-input"
                  v-model="editProfile.email"
                  type="email"
                  :class="[
                    'w-full bg-background border-0 rounded-full px-4 py-3 focus:ring-2 focus:ring-primary focus:outline-hidden text-sm text-text',
                    emailError ? 'ring-2 ring-warning' : ''
                  ]"
                  autocomplete="off"
                  @blur="validateEmail"
                />
                <p v-if="emailError" class="text-warning text-xs mt-1">{{ emailError }}</p>
              </div>
              <!-- Show API errors -->
              <div v-if="error" class="bg-warning/10 border border-warning rounded-lg p-3">
                <p class="text-warning text-sm">{{ error }}</p>
              </div>
              <div class="flex space-x-3">
                <button
                  type="submit"
                  :disabled="!isDirty || emailError || loading"
                  :class="[
                    'rounded-full px-6 py-2 font-medium flex items-center',
                    (!isDirty || emailError || loading)
                      ? 'bg-accent/30 text-text-inverse/50 cursor-not-allowed'
                      : 'bg-accent text-text-inverse hover:bg-accent-dark transition-colors'
                  ]"
                >
                  <i v-if="loading" class="fa-solid fa-spinner fa-spin mr-2"></i>
                  <i v-else class="fa-solid fa-save mr-2"></i>
                  {{ loading ? 'Saving...' : 'Save Changes' }}
                </button>
                <button
                  type="button"
                  @click="discardChanges"
                  :disabled="!isDirty || loading"
                  :class="[
                    'rounded-full px-6 py-2 font-medium flex items-center',
                    (!isDirty || loading)
                      ? 'bg-background/50 text-text-light/50 cursor-not-allowed'
                      : 'bg-background text-text-light hover:bg-background-hover transition-colors'
                  ]"
                >
                  <i class="fa-solid fa-times mr-2"></i>
                  Discard
                </button>
              </div>
            </form>
          </div>

          <!-- Account Actions -->
          <div class="mb-6">
            <h4 class="text-text font-medium mb-4 flex items-center">
              <i class="fa-solid fa-cog mr-2"></i>
              Account Actions
            </h4>
            <div class="space-y-3">
              <button type="button" @click="openChangePasswordModal"
                class="w-full bg-background rounded-full px-4 py-2 text-text-light hover:bg-background-hover transition-colors text-left flex items-center">
                <i class="fa-solid fa-key mr-2"></i>
                Change Password
              </button>
              <button type="button" @click="logout"
                class="w-full bg-background rounded-full px-4 py-2 text-text-light hover:bg-warning hover:text-text-inverse transition-colors text-left flex items-center">
                <i class="fa-solid fa-sign-out-alt mr-2"></i>
                Logout
              </button>
            </div>
          </div>

          <!-- Theme Settings -->
          <div>
            <h4 class="text-text font-medium mb-4 flex items-center">
              <i class="fa-solid fa-palette mr-2"></i>
              Theme Options
            </h4>
            <div class="space-y-3">
              <button
                type="button"
                :class="[
                  'flex items-center justify-between w-full bg-background rounded-full px-4 py-2 transition-colors',
                  theme === 'light' ? 'ring-2 ring-primary bg-primary/10' : 'hover:bg-background-hover',
                ]"
                @click="theme = 'light'"
                aria-pressed="theme === 'light'"
              >
                <div class="flex items-center">
                  <i class="fa-solid fa-sun text-accent mr-2"></i>
                  <span class="text-text">Light</span>
                </div>
                <span v-if="theme === 'light'" class="fa-solid fa-check text-primary ml-2"></span>
              </button>
              <button
                type="button"
                :class="[
                  'flex items-center justify-between w-full bg-background rounded-full px-4 py-2 transition-colors',
                  theme === 'dark' ? 'ring-2 ring-primary bg-primary/10' : 'hover:bg-background-hover',
                ]"
                @click="theme = 'dark'"
                aria-pressed="theme === 'dark'"
              >
                <div class="flex items-center">
                  <i class="fa-solid fa-moon text-primary mr-2"></i>
                  <span class="text-text">Dark</span>
                </div>
                <span v-if="theme === 'dark'" class="fa-solid fa-check text-primary ml-2"></span>
              </button>
              <button
                type="button"
                :class="[
                  'flex items-center justify-between w-full bg-background rounded-full px-4 py-2 transition-colors',
                  theme === 'system' ? 'ring-2 ring-primary bg-primary/10' : 'hover:bg-background-hover',
                ]"
                @click="theme = 'system'"
                aria-pressed="theme === 'system'"
              >
                <div class="flex items-center">
                  <i class="fa-solid fa-adjust text-text-light mr-2"></i>
                  <span class="text-text">Auto</span>
                </div>
                <span v-if="theme === 'system'" class="fa-solid fa-check text-primary ml-2"></span>
              </button>
            </div>
          </div>
        </div>
        <!-- End User Details Form -->
      </div>
    </Card>
    <ChangePasswordModal
      :visible="showChangePasswordModal"
      @close="showChangePasswordModal = false"
      @success="showChangePasswordModal = false"
    />
  </Page>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useProfileStore } from '@/shared/stores/profile';
import { storeToRefs } from 'pinia';
import ChangePasswordModal from '../components/ChangePasswordModal.vue';

import Avatar from 'primevue/avatar';
import { useUserAvatar } from '@/shared/composables/useUserAvatar';
import { useAuth } from '@/shared/composables/useAuth';

// Profile store
const profileStore = useProfileStore();
const { profile, theme, loading, error } = storeToRefs(profileStore);

// Auth store
const authStore = useAuth();

// Editable profile state
const editProfile = ref({
  name: profile.value.name,
  designation: profile.value.designation,
  email: profile.value.email,
});

// Track email validation error
const emailError = ref<string | null>(null);

// Watch for store changes and update editProfile
watch(profile, (newProfile) => {
  editProfile.value = {
    name: newProfile.name,
    designation: newProfile.designation,
    email: newProfile.email,
  };
  emailError.value = null;
});

// Check if form is dirty
const isDirty = computed(() => {
  return (
    editProfile.value.name !== profile.value.name ||
    editProfile.value.designation !== profile.value.designation ||
    editProfile.value.email !== profile.value.email
  );
});

// Email validation
function validateEmail() {
  const email = editProfile.value.email.trim();
  if (!email) {
    emailError.value = 'E-mail is required.';
    return false;
  }
  // Simple email regex
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    emailError.value = 'Please enter a valid e-mail address.';
    return false;
  }
  emailError.value = null;
  return true;
}

// Save profile changes
async function saveProfile() {
  if (!validateEmail()) return;
  
  const updatedProfile = {
    ...profile.value,
    ...editProfile.value,
  };
  
  await profileStore.updateProfile(updatedProfile);
}

// Discard changes
function discardChanges() {
  editProfile.value = {
    name: profile.value.name,
    designation: profile.value.designation,
    email: profile.value.email,
  };
  emailError.value = null;
}


// User initials and avatar color (same as Sidebar)
const userName = computed(() => profile.value.name || 'John Doe');
const { initials: userInitials, colorClass: userAvatarColor } = useUserAvatar(userName);

const showChangePasswordModal = ref(false);

function openChangePasswordModal() {
  showChangePasswordModal.value = true;
}

async function logout() {
  await authStore.logout();
  window.location.href = '/login';
}

definePage({
  meta: {
    title: 'User Profile',
    requiresProject: false,
  },
});
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>
